import { doc as docRef, writeBatch } from 'firebase/firestore';
import { db as defaultDb } from '../firebase';
import { reorder } from './ordering';

// Persisting a reorder of a project's stored footage clips.
//
// The preflight decides the FIRST order (ordering.js, before a byte moves);
// this is the same decision made again after the clips are already in
// Firestore, so it writes rather than sorts. Owner-writable per
// firestore.rules — `match /videos/{videoId} { allow read, write: if
// isProjectOwner(projectId) }` — so no callable is needed and the reorder lands
// in one round trip.
//
// One batch, not N updates: the clips are a SEQUENCE, and a half-applied
// reorder is a scrambled timelapse with no error to explain it. A batch either
// lands whole or not at all.

/**
 * Move the clip at `fromIndex` to `toIndex` and persist the resulting order.
 *
 * Returns the reordered list (for an optimistic render) and the writes it
 * issued. Only clips whose stored `order` actually changes are written — a
 * one-position swap in a 100-clip project is 2 writes, not 100.
 *
 * Orders are re-stamped 0-based and contiguous, which also repairs the gaps a
 * partially-failed batch leaves behind ({0,1,2,5} → {0,1,2,3}). That is safe
 * only while nothing else is stamping orders, which is why the caller disables
 * reordering during an upload: an in-flight batch derives its orders from
 * `nextOrderBase` (max + 1) read BEFORE the upload started, so renumbering
 * underneath it would hand an arriving clip an order an existing clip now
 * holds.
 */
export async function persistFootageOrder(projectId, videos, fromIndex, toIndex, {
  db = defaultDb,
  doc = docRef,
  batchFactory = writeBatch,
} = {}) {
  const current = videos || [];
  const next = reorder(current, fromIndex, toIndex);
  const changed = next
    .map((video, index) => ({ video, index }))
    .filter(({ video, index }) => Number(video?.order) !== index);

  if (!projectId || changed.length === 0) return { videos: next, written: 0 };

  const batch = batchFactory(db);
  for (const { video, index } of changed) {
    batch.update(doc(db, 'projects', projectId, 'videos', video.id), { order: index });
  }
  await batch.commit();
  return { videos: next, written: changed.length };
}

export default persistFootageOrder;
